﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as api from "~base/Services/API";
import * as sessionModel from "~base/Services/API/Session/Model";

//-------------------------------------------------------------------------------------
// Http Contract
//-------------------------------------------------------------------------------------

const uri = (
    salesOpportunity?: string,
    subjectIdentifier?: string,
    externalInteractionId?: string
): string => {
    const baseUri = "api/Guide/v1/Sessions/StartInfo";
    const params = new URLSearchParams();

    if (salesOpportunity) {
        params.append("salesOpportunity", salesOpportunity);
    }

    if (subjectIdentifier) {
        params.append("subjectIdentifier", subjectIdentifier);
    }

    if (externalInteractionId) {
        params.append("externalInteractionId", externalInteractionId);
    }

    const queryString = params.toString();
    return queryString ? `${baseUri}?${queryString}` : baseUri;
};

const method = "GET";

//-------------------------------------------------------------------------------------
// Types
//-------------------------------------------------------------------------------------

export interface GetStartInfoContext {
    salesOpportunity?: string;
    subjectIdentifier?: string;
    externalInteractionId?: string;
}

interface GetStartInfoProps
    extends
        GetStartInfoContext,
        api.ApiHookBaseProps<
            unknown,
            sessionModel.GetStartInfoResponse,
            unknown
        > {}

//-------------------------------------------------------------------------------------
// API Hook
//-------------------------------------------------------------------------------------

export function useGetSessionStartInfo(
    props: GetStartInfoProps
): [boolean, (params: GetStartInfoContext) => Promise<unknown>] {
    const apiParams: api.ApiHookProps<
        unknown,
        sessionModel.GetStartInfoResponse,
        unknown
    > = {
        ...props,
        uri: uri(
            props.salesOpportunity,
            props.subjectIdentifier,
            props.externalInteractionId
        ),
        method: method,
    };

    const [loading, invoker] = api.useApi<
        unknown,
        sessionModel.GetStartInfoResponse,
        unknown
    >(apiParams);
    return [
        loading,
        (params: GetStartInfoContext) =>
            invoker(
                uri(
                    params.salesOpportunity,
                    params.subjectIdentifier,
                    params.externalInteractionId
                ),
                undefined
            ),
    ];
}
